import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { formatCAD } from '../utils/currency';
import { 
  ArrowDownCircle, 
  Loader2, 
  CheckCircle,
  ArrowLeft,
  DollarSign,
  Building,
  User,
  Hash
} from 'lucide-react';

export default function WithdrawPage() {
  const { t } = useLanguage();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [balance, setBalance] = useState(0);
  const [formData, setFormData] = useState({
    amount: '',
    account_name: '',
    account_number: '',
    institution: '',
    transit: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [withdrawResult, setWithdrawResult] = useState(null);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await api.get('/wallet/balance');
        setBalance(response.data.balance);
      } catch (error) {
        logger.error('Error fetching balance:', error);
      }
    };
    fetchBalance();
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount > balance) {
      toast.error(t('transfer.insufficient'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/wallet/withdraw', {
        amount: amount,
        method: 'bank_transfer',
        bank_details: {
          account_name: formData.account_name,
          account_number: formData.account_number,
          institution: formData.institution,
          transit: formData.transit
        }
      });
      
      setWithdrawResult(response.data);
      setSuccess(true);
      toast.success(t('withdraw.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card className="text-center animate-fade-in-up">
            <CardContent className="pt-12 pb-8">
              <div className="h-20 w-20 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t('withdraw.success')}</h2>
              <p className="text-muted-foreground mb-2">{t('withdraw.success_message')}</p>
              <p className="font-bold text-xl mb-2">{formatCAD(parseFloat(formData.amount))}</p>
              {withdrawResult?.reference && (
                <p className="text-sm text-muted-foreground mb-4">
                  {t('transactions.reference')}: {withdrawResult.reference}
                </p>
              )}
              <p className="text-xs text-muted-foreground mb-8">{t('withdraw.processing_time')}</p>
              <Button onClick={() => navigate('/dashboard')} className="rounded-full px-8" data-testid="back-to-dashboard">
                {t('topup.back_to_dashboard')}
              </Button>
            </CardContent>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      
      <main className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('dashboard.welcome').split(',')[0]}
        </Button>

        <Card className="animate-fade-in-up">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <ArrowDownCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <CardTitle>{t('withdraw.title')}</CardTitle>
                <CardDescription>{t('withdraw.subtitle')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Available Balance */}
              <div className="bg-muted/50 rounded-xl p-4 flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('transfer.available')}</span>
                <span className="font-bold text-lg">{formatCAD(balance)}</span>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label htmlFor="amount">{t('withdraw.amount')}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="amount"
                    type="number"
                    placeholder="0.00"
                    className="pl-10 h-12 rounded-xl"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    min="1"
                    step="0.01"
                    max={balance}
                    required
                    data-testid="withdraw-amount"
                  />
                </div>
              </div>

              {/* Bank Details */}
              <div className="space-y-4 pt-2">
                <Label className="text-base font-semibold">{t('withdraw.bank_details')}</Label>
                
                <div className="space-y-2">
                  <Label htmlFor="account_name">{t('withdraw.account_name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="account_name"
                      type="text"
                      placeholder="John Doe"
                      className="pl-10 h-12 rounded-xl"
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      required
                      data-testid="withdraw-account-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account_number">{t('withdraw.account_number')}</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="account_number"
                      type="text"
                      placeholder="1234567890"
                      className="pl-10 h-12 rounded-xl"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      required
                      data-testid="withdraw-account-number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="institution">{t('withdraw.institution')}</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="institution"
                        type="text"
                        placeholder="001"
                        className="pl-10 h-12 rounded-xl"
                        value={formData.institution}
                        onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                        required
                        data-testid="withdraw-institution"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="transit">{t('withdraw.transit')}</Label>
                    <Input
                      id="transit"
                      type="text"
                      placeholder="12345"
                      className="h-12 rounded-xl"
                      value={formData.transit}
                      onChange={(e) => setFormData({ ...formData, transit: e.target.value })}
                      required
                      data-testid="withdraw-transit"
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">{t('withdraw.processing_time')}</p>

              <Button 
                type="submit"
                className="w-full h-12 rounded-xl" 
                disabled={loading || !formData.amount || parseFloat(formData.amount) > balance}
                data-testid="withdraw-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <ArrowDownCircle className="mr-2 h-4 w-4" />
                    {t('withdraw.withdraw')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>

      <MobileNav />
    </div>
  );
}
